import axios from "axios";
import { useState, useEffect } from "react";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import { useNavigate } from "react-router-dom";

function AddPersonalStock() {
  const navigate = useNavigate();
  const toast = useToast();

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]); // Changed from typeGetting
  const [filteredProducts, setFilteredProducts] = useState([]); // For category filtering
  const [editingIndex, setEditingIndex] = useState(null);

  const [addData, setAddData] = useState({
    item: "",
    quantity: "",
    category: "",
    per_qty_weight: "",
    weight: "",
    date: new Date().toISOString().split("T")[0],
    created_by: "",
    created_date: "",
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Filter products based on selected category
  useEffect(() => {
    if (addData.category) {
      const filtered = products.filter(product => 
        product.category === addData.category
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [addData.category, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/product.php`);
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching products:", error.message);
      setProducts([]);
      toast.error("Error fetching products", 3000, "topRight");
    }
  };

  const handleAddItem = () => {
    if (
      !addData.category ||
      !addData.item ||
      !addData.quantity ||
      !addData.per_qty_weight
    ) {
      toast.error("Please fill all required fields", 3000, "topRight");
      return;
    }
    const qty = parseInt(addData.quantity) || 0;
    const perQtyWt = parseFloat(addData.per_qty_weight) || 0;
    const totalWeight = (qty * perQtyWt).toFixed(2);

    // Get the selected product details
    const selectedProduct = products.find((product) => String(product.id) === String(addData.item));
    const productName = selectedProduct
      ? (selectedProduct.item_name_english || selectedProduct.item_name)
      : addData.item;
    const productTamil = selectedProduct?.item_name_tamil || "";

    const newItem = {
      date: addData.date,
      status: "In Stock",
      item: productName,
      item_tamil: productTamil,
      product_id: addData.item,
      quantity: qty,
      per_qty_weight: perQtyWt,
      category: addData.category,
      weight: totalWeight,
      created_by: "",
      created_date: "",
      updated_by: "",
      updated_date: "",
    };

    if (editingIndex !== null) {
      const updatedItems = [...items];
      updatedItems[editingIndex] = newItem;
      setItems(updatedItems);
      setEditingIndex(null);
    } else {
      setItems([...items, newItem]);
    }

    setAddData((prev) => ({
      ...prev,
      item: "",
      quantity: "",
      per_qty_weight: "",
      weight: "",
      category: "",
    }));
  };

  const handleEditItem = (index) => {
    const itemToEdit = items[index];
    setAddData({
      ...addData,
      item: itemToEdit.product_id || itemToEdit.item,
      quantity: itemToEdit.quantity.toString(),
      category: itemToEdit.category,
      per_qty_weight: itemToEdit.per_qty_weight ? itemToEdit.per_qty_weight.toString() : "",
      weight: itemToEdit.weight,
      date: itemToEdit.date,
    });
    setEditingIndex(index);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    setEditingIndex(null);
  };

  // Calculate totals for the overall stock
  const calculateTotals = () => {
    const totalQuantity = items.reduce(
      (sum, item) => sum + parseInt(item.quantity || 0),
      0
    );
    const totalWeight = items.reduce(
      (sum, item) => sum + parseFloat(item.weight || 0),
      0
    );

    return {
      total_quantity: totalQuantity,
      total_weight: totalWeight.toFixed(2),
      avaliable_quantity: totalQuantity,
      sold_quantity: 0,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.error("Please add at least one item", 3000, "topRight");
      return;
    }

    try {
      const totals = calculateTotals();

      // Aggregate items by category and item for the overall stock
      const groupedItems = items.reduce((acc, item) => {
        const key = `${item.category}-${item.item}`;
        if (!acc[key]) {
          acc[key] = {
            item: item.item,
            category: item.category,
            quantity: 0,
            weight: 0,
            total_quantity: 0,
            avaliable_quantity: 0,
            sold_quantity: 0,
          };
        }
        acc[key].quantity += parseInt(item.quantity);
        acc[key].weight += parseFloat(item.weight);
        acc[key].total_quantity += parseInt(item.quantity);
        acc[key].avaliable_quantity += parseInt(item.quantity);
        return acc;
      }, {});

      const mainCategory = items[0]?.category || "";
      const mainItemName =
        Object.values(groupedItems).length === 1
          ? items[0].item
          : `${mainCategory} Items`;

      const payload = {
        item: mainItemName,
        quantity: totals.total_quantity.toString(),
        amount: "0",
        category: mainCategory,
        weight: `${totals.total_weight}g`,
        avaliable_quantity: totals.avaliable_quantity.toString(),
        total_quantity: totals.total_quantity.toString(),
        sold_quantity: totals.sold_quantity.toString(),
        created_by: "",
        created_date: "",
        updated_by: "",
        updated_date: "",
        personal_stock_list: items.map((item) => ({
          date: item.date,
          status: item.status,
          item: item.item,
          item_tamil: item.item_tamil,
          product_id: item.product_id,
          quantity: item.quantity.toString(),
          category: item.category,
          weight: `${item.per_qty_weight}g`,   // per-piece weight; API calculates total as qty × weight
          created_by: "",
          created_date: "",
          updated_by: "",
          updated_date: "",
        })),
      };

      console.log("Submit payload:", payload);

      const response = await axios.post(`${ApiUrl}/Api/Personal_stock.php`, payload);
      console.log("Submit response:", response);

      toast.success("Stock added successfully!", 3000, "topRight");
      setItems([]);
      navigate("/personal-stock");
    } catch (error) {
      console.error("Error submitting data:", error.message);
      toast.error("Error submitting data", 3000, "topRight");
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="card card-body">
          <h6 className="mb-3">Add Stock Items</h6>
          <div className="row">
            <div className="col-md-2">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={addData.date}
                placeholder="Date"
                onChange={(e) =>
                  setAddData({ ...addData, date: e.target.value })
                }
                required
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={addData.category}
                onChange={(e) =>
                  setAddData({ ...addData, category: e.target.value })
                }
                required
              >
                <option value="">Select Category</option>
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Item Name</label>
              <select
                className="form-select"
                value={addData.item}
                onChange={(e) => {
                  setAddData({ ...addData, item: e.target.value });
                }}
                disabled={!addData.category}
                required
              >
                <option value="">Select Item</option>
                {filteredProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.item_name_tamil
                      ? `${product.item_name_english || product.item_name} / ${product.item_name_tamil}`
                      : (product.item_name_english || product.item_name)}
                  </option>
                ))}
              </select>
              {!addData.category && (
                <small className="text-muted text-danger">Please select category first</small>
              )}
            </div>

            <div className="col-md-1">
              <label className="form-label">Quantity</label>
              <input
                type="tel"
                className="form-control"
                placeholder="Quantity"
                value={addData.quantity}
                onChange={(e) => {
                  const qty = e.target.value;
                  const totalWt = (parseFloat(qty || 0) * parseFloat(addData.per_qty_weight || 0)).toFixed(2);
                  setAddData({ ...addData, quantity: qty, weight: totalWt });
                }}
                required
              />
            </div>

            <div className="col-md-1">
              <label className="form-label">P.Q.Weight(g)</label>
              <input
                type="tel"
                className="form-control"
                placeholder="Weight per piece"
                value={addData.per_qty_weight}
                onChange={(e) => {
                  const perWt = e.target.value;
                  const totalWt = (parseFloat(addData.quantity || 0) * parseFloat(perWt || 0)).toFixed(2);
                  setAddData({ ...addData, per_qty_weight: perWt, weight: totalWt });
                }}
                required
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">TotalWeight(g)</label>
              <input
                type="text"
                className="form-control bg-light"
                placeholder="Auto calculated"
                value={addData.weight ? `${addData.weight}g` : ""}
                readOnly
              />
            </div>

            <div className="col-md-1 d-flex align-items-center mt-2">
              <button
                className={`btn btn-sm w-100 ${
                  editingIndex === null ? "btn-success" : "btn-primary"
                }`}
                onClick={handleAddItem}
                type="button"
              >
                <i
                  className={`bi ${
                    editingIndex === null ? "bi-plus" : "bi-check"
                  }`}
                ></i>
              </button>
            </div>

            <div className="col-md-1 d-flex mt-2 align-items-center">
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={items.length === 0}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid py-3">
        <div className="card shadow-sm">
          <div className="card-body">
            {items.length > 0 && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="alert alert-info">
                    <strong>Summary:</strong> 
                    Total Items: {items.length} | 
                    Total Quantity: {items.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0)} | 
                    Total Weight: {items.reduce((sum, item) => sum + parseFloat(item.weight || 0), 0).toFixed(2)}g
                  </div>
                </div>
              </div>
            )}
            
            <div className="card tableCard p-2">
              <div className="table-responsive">
                <table className="table">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No</th>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Item Name</th>
                      <th>Quantity</th>
                      <th>Per Qty Weight (g)</th>
                      <th>Total Weight (g)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length > 0 ? (
                      items.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.date}</td>
                          <td>{item.category}</td>
                          <td>{item.item_tamil ? `${item.item} / ${item.item_tamil}` : item.item}</td>
                          <td>{item.quantity}</td>
                          <td>{item.per_qty_weight}g</td>
                          <td>{item.weight}g</td>
                          <td>
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() => handleEditItem(index)}
                            >
                              <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <i className="fa-solid fa-trash text-danger fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No items added yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddPersonalStock;